/**
 * PDP How-to Video widget (floating bubble → expandable dialog).
 *
 * Reparents the SAME <video> element between the collapsed thumb and the
 * expanded dialog slot instead of using two separate <video> tags, so
 * playback never restarts across open/close — matches the requirement that
 * the video is always playing in both states.
 */
(function () {
  'use strict';

  var SEL_ROOT = '[data-how-to-video]';

  function boot() {
    document.querySelectorAll(SEL_ROOT).forEach(initWidget);
  }

  function initWidget(root) {
    var video = root.querySelector('[data-htv-video]');
    var thumb = root.querySelector('[data-htv-thumb]');
    var dialog = root.querySelector('[data-htv-dialog]');
    var videoSlot = root.querySelector('[data-htv-video-slot]');
    if (!video || !thumb || !dialog || !videoSlot) return;

    // A `position: fixed` element only tracks the real viewport if none of
    // its ancestors create a containing block (transform/filter/etc) — this
    // markup is rendered deep inside the product section, and on mobile that
    // showed up as the open dialog drifting/overlapping the header while
    // scrolling instead of staying put. Moving it to a direct child of
    // <body> guarantees it's positioned against the viewport, not whatever
    // ancestor happens to be trapping it.
    //
    // Inserted FIRST (not appended last): the widget's z-index is tied with
    // the cart drawer's (see how-to-video.liquid), and ties resolve to
    // whichever is later in the DOM — the cart drawer is already further
    // down the static page markup, so putting this widget first guarantees
    // the cart still wins and renders on top of it when opened.
    if (root.parentElement !== document.body || document.body.firstChild !== root) {
      document.body.insertBefore(root, document.body.firstChild);
    }

    video.muted = true;
    video.play().catch(function () {});

    root.querySelector('[data-htv-open]').addEventListener('click', open);
    root.querySelector('[data-htv-collapse]').addEventListener('click', collapse);
    root.querySelector('[data-htv-dismiss]').addEventListener('click', function (e) {
      e.stopPropagation();
      root.remove();
    });
    root.querySelector('[data-htv-close]').addEventListener('click', function () {
      root.remove();
    });

    function open() {
      videoSlot.appendChild(video);
      video.muted = false;
      dialog.hidden = false;
      root.classList.add('is-open');
    }

    function collapse() {
      thumb.appendChild(video);
      video.muted = true;
      if (video.paused) video.play().catch(function () {});
      dialog.hidden = true;
      root.classList.remove('is-open');
    }

    // ─── Play / pause ────────────────────────────────────────────────────
    var playBtn = root.querySelector('[data-htv-playpause]');
    var iconPause = root.querySelector('[data-htv-icon-pause]');
    var iconPlay = root.querySelector('[data-htv-icon-play]');

    function updatePlayIcon() {
      iconPause.hidden = video.paused;
      iconPlay.hidden = !video.paused;
    }

    playBtn.addEventListener('click', function () {
      if (video.paused) {
        video.play().catch(function () {});
      } else {
        video.pause();
      }
    });
    video.addEventListener('play', updatePlayIcon);
    video.addEventListener('pause', updatePlayIcon);
    updatePlayIcon();

    // ─── Mute toggle ─────────────────────────────────────────────────────
    var muteBtn = root.querySelector('[data-htv-mute]');
    var iconMuted = root.querySelector('[data-htv-icon-muted]');
    var iconUnmuted = root.querySelector('[data-htv-icon-unmuted]');

    function updateMuteIcon() {
      iconMuted.hidden = !video.muted;
      iconUnmuted.hidden = video.muted;
    }

    muteBtn.addEventListener('click', function () {
      video.muted = !video.muted;
      updateMuteIcon();
    });
    video.addEventListener('volumechange', updateMuteIcon);
    updateMuteIcon();

    // ─── Rewind / forward 5s ─────────────────────────────────────────────
    root.querySelector('[data-htv-rewind]').addEventListener('click', function () {
      video.currentTime = Math.max(0, video.currentTime - 5);
    });
    root.querySelector('[data-htv-forward]').addEventListener('click', function () {
      var max = video.duration || video.currentTime;
      video.currentTime = Math.min(max, video.currentTime + 5);
    });

    // ─── Seek bar + time display ─────────────────────────────────────────
    var seek = root.querySelector('[data-htv-seek]');
    var timeEl = root.querySelector('[data-htv-time]');
    var scrubbing = false;

    function formatTime(seconds) {
      if (!isFinite(seconds) || seconds < 0) return '0:00';
      var m = Math.floor(seconds / 60);
      var s = Math.floor(seconds % 60);
      return m + ':' + (s < 10 ? '0' : '') + s;
    }

    function updateTimeText(seconds) {
      timeEl.textContent = formatTime(seconds) + ' / ' + formatTime(video.duration);
    }

    function updateSeekUI() {
      if (scrubbing || !video.duration) return;
      var pct = (video.currentTime / video.duration) * 100;
      seek.value = pct;
      seek.style.setProperty('--htv-seek-pct', pct + '%');
      updateTimeText(video.currentTime);
    }

    video.addEventListener('timeupdate', updateSeekUI);
    video.addEventListener('loadedmetadata', updateSeekUI);

    seek.addEventListener('input', function () {
      scrubbing = true;
      seek.style.setProperty('--htv-seek-pct', seek.value + '%');
      // Update the time text immediately on drag — updateSeekUI() (driven by
      // the video's own 'timeupdate' event) is deliberately skipped while
      // scrubbing, which otherwise left the displayed time frozen until the
      // mouse/finger was released instead of tracking the drag live.
      if (video.duration) {
        video.currentTime = (seek.value / 100) * video.duration;
        updateTimeText(video.currentTime);
      }
    });
    seek.addEventListener('change', function () {
      scrubbing = false;
    });

    // ─── Add to cart ─────────────────────────────────────────────────────
    // Delegates to the page's own Add to Cart button instead of POSTing
    // /cart/add.js ourselves — this product often has a selling plan
    // (subscription), free gifts, or a colour bundle selection active, all
    // of which the real button's own click handler already knows how to
    // build correctly. Reimplementing that here would only ever add the
    // bare default variant and silently drop all of it.
    var atcBtn = root.querySelector('[data-htv-atc]');
    atcBtn.addEventListener('click', function () {
      // NOT `form[id*="product-form"]` — the page also has an
      // installment/financing form whose id matches that same substring and
      // sits earlier in the DOM (so querySelector picked it first), and it
      // has no Add to Cart button in it at all, silently doing nothing.
      // `.main-product-form` is the class Minimog itself puts only on the
      // real form (the sticky bar's duplicate form uses `.sticky-atc-form`
      // instead).
      var form = document.querySelector('form.main-product-form');
      var mainAtcBtn = form && form.querySelector('[name="add"], button[type="submit"].add-to-cart');
      if (!mainAtcBtn) return;

      video.pause();

      // The real button shows its own spinner while the request is in
      // flight, but that's easy to miss since this button (and the video
      // dialog around it) is what the user is actually looking at — mirror
      // the same loading state here. Cleared on Minimog's own
      // 'ON_ITEM_ADDED' event (emitted once the add genuinely finishes),
      // with a timeout fallback in case that event never comes for some
      // reason, so this can't get stuck disabled forever.
      atcBtn.disabled = true;
      atcBtn.classList.add('is-loading');

      var unsubscribe;
      var stopLoading = function () {
        clearTimeout(safetyTimer);
        if (unsubscribe) unsubscribe();
        atcBtn.disabled = false;
        atcBtn.classList.remove('is-loading');
      };
      var safetyTimer = setTimeout(stopLoading, 8000);

      if (window.MinimogEvents && typeof window.MinimogEvents.subscribe === 'function') {
        unsubscribe = window.MinimogEvents.subscribe('ON_ITEM_ADDED', stopLoading);
      }

      mainAtcBtn.click();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
}());
